import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { colors } from "utils";

// The Download control the Sessions and Dosage & Collaboration tabs share
// (frame 4729:69482).

export const DownloadMenu = ({ options }) => {
  const [open, setOpen] = useState(false);
  const [firstFocus, setFirstFocus] = useState(0);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    itemRefs.current[firstFocus]?.focus();
    const closeFromOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeFromOutside);
    return () => document.removeEventListener("mousedown", closeFromOutside);
  }, [open, firstFocus]);

  const openAt = (index) => {
    setFirstFocus(index);
    setOpen(true);
  };

  const onButtonKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openAt(0);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      openAt(options.length - 1);
    }
  };

  const moveFocus = (step) => {
    const current = itemRefs.current.indexOf(document.activeElement);
    const next = (current + step + options.length) % options.length;
    itemRefs.current[next]?.focus();
  };

  const onMenuKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(event.key === "ArrowDown" ? 1 : -1);
    }
    if (event.key === "Escape") {
      setOpen(false);
      buttonRef.current?.focus();
    }
    if (event.key === "Tab") setOpen(false);
  };

  return (
    <Wrap ref={wrapRef}>
      <MenuButton
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openAt(0))}
        onKeyDown={onButtonKeyDown}
      >
        <ButtonLabel>Download</ButtonLabel>
        <ChevronGlyph />
      </MenuButton>
      {open && (
        <Menu role="menu" onKeyDown={onMenuKeyDown}>
          {options.map((option, index) => (
            <MenuItem
              key={option.label}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              role="menuitem"
              type="button"
              tabIndex={-1}
              onClick={() => {
                setOpen(false);
                option.onSelect();
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  position: relative;
`;

// Button/Primary: 48px tall, Inter Bold 18/26 in off-white on the brand blue,
// padded 16px before the label and 8px after the chevron.
const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 8px 0 16px;
  height: 48px;
  border-radius: 8px;
  border: none;
  background-color: ${colors.blue500};
  color: ${colors.milkGlass500};
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: 0.25px;
  cursor: pointer;
  white-space: nowrap;
`;

// The label sits 8px inside its own box, as the frame nests it.
const ButtonLabel = styled.span`
  padding: 0 8px;
`;

// The design's down chevron, drawn as exported.
const ChevronGlyph = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9L12 15L18 9"
      stroke="#FFFEFE"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Menu/Sort: opens directly below the button and extends to its left. The
// design strokes its 1px ring INSIDE the 232 x 80 menu, over the two 40px
// items, so the ring is drawn on top of them rather than as a border that
// would add 2px.
const Menu = styled.div`
  position: absolute;
  top: calc(100% + 1px);
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 232px;
  overflow: hidden;
  background: #ffffff;
  border-radius: 6px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 1px solid ${colors.blue300};
    border-radius: 6px;
    pointer-events: none;
  }
`;

// 40px items: 8px above and below a 24px line.
const MenuItem = styled.button`
  padding: 8px 16px;
  border: none;
  background: none;
  text-align: left;
  white-space: nowrap;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 24px;
  color: ${colors.nightlyWoods500};
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${colors.blue50};
  }
`;
